import { IValidationOptional } from '@legacy/types';
import { validateJson } from '@common/utils/json-validation';
import i18n from 'i18next';

const MAX_VARCHAR_LENGTH = 191;

export const PASSWORD_LENGTH = {
  DEFAULT: 8,
  ADMIN: 12,
};

export const REGEX = {
  zip: /^[0-9]+$/,
  phone: /^[+0-9\s()+]+$/,
  email:
    /^(([^<>()[\]\\,;!#$%^&*'"?=/:äöüÄÖÜß\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
  password:
    /^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9])(?=.*?[±™©Ðþ×ÏÎÞÅ®Øø¯ß€ŠšƒŒ¢¥œ£µ¡§†‡+#¿?!@$%‰^ˆ&*°•·_¬˜=‹›«»„“”"…‘’¨`´¸'~^¦|\\/()[\]{},.;:><–—-]).{8,}$/,
  password_admin:
    /^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9])(?=.*?[±™©Ðþ×ÏÎÞÅ®Øø¯ß€ŠšƒŒ¢¥œ£µ¡§†‡+#¿?!@$%‰^ˆ&*°•·_¬˜=‹›«»„“”"…‘’¨`´¸'~^¦|\\/()[\]{},.;:><–—-]).{12,}$/,
  domain:
    /^((?:(?:(?:[\wäöü][-.+]?)*[\wäöü])+)((?:(?:(?:[\wäöü][-.+]?){0,62})[\wäöü])+)\.([\wäöü]{2,6})$)/,
  noWww: /^(?!www\.).+/,
};

const isEmpty = (val: InputValue): boolean => {
  if (typeof val === 'string') {
    return !val.trim();
  }
  return val === null || val === undefined || !val;
};

type InputValue = string | undefined | null;
type ValidatorFn = (val: InputValue, optional?: IValidationOptional) => string | null;

const validators: Record<string, ValidatorFn> = {
  'firstname': val => (isEmpty(val) ? 'firstname' : null),
  'zip': val => (!REGEX.zip.test(val ?? '') ? 'zip' : null),
  'phone': val => (!REGEX.phone.test(val ?? '') ? 'phone' : null),
  'email': val => (isEmpty(val) ? null : !REGEX.email.test(val ?? '') ? 'email' : null),
  'password': val => (!REGEX.password.test(val ?? '') ? 'password' : null),
  'psw': val => (!REGEX.password.test(val ?? '') ? 'psw' : null),
  'password_admin': val => (!REGEX.password_admin.test(val ?? '') ? 'password' : null),
  'domain': val => (!REGEX.domain.test(val ?? '') ? 'domain' : null),
  'should-not-start-with-www': val => (!REGEX.noWww.test(val ?? '') ? 'should-not-start-with-www' : null),
  'required': val => (isEmpty(val) ? 'required' : null),
  'max_varchar_length': val => ((val ?? '').length > MAX_VARCHAR_LENGTH ? 'max_varchar_length' : null),
  'min_length': (val, optional) =>
    optional?.min_length && (val ?? '').length < optional.min_length ? 'min_length' : null,
  'website': val => (isEmpty(val) ? 'website' : null),
  'salutation': val => (isEmpty(val) ? 'salutation' : null),
  'json': val => (validateJson(val ?? '') !== null ? 'json' : null),
};

/**
 * Creates a validation function that checks a value against a set of rules.
 *
 * @param validations - An array of validation rule names.
 * @param optional - Optional configuration for certain validations (e.g., min_length).
 * @returns A function that takes a value and returns an array of error keys or null if valid.
 */
export const validate = (
  validations: (string | undefined)[],
  optional?: IValidationOptional
) => {
  return (val: InputValue): string[] | null => {
    const errors: string[] = [];

    validations.filter(Boolean).forEach(rule => {
      const validateRule = validators[rule!];
      if (!validateRule) {
        throw new Error(`${i18n.t('common.error-appeared')} "${rule}"`);
      }
      const result = validateRule(val, optional);
      if (result) errors.push(result);
    });

    return errors.length ? errors : null;
  };
};
